import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import CategorySection from './components/CategorySection';
import ContentSections from './components/ContentSections';
import ArticleCTA from './components/ArticleCTA';
import Footer from './components/Footer';
import { categories } from '@/mocks/categories';
import { pillarArticles } from '@/mocks/articles';
import JsonLd from '@/seo/JsonLd';
import { buildWebSiteSchema } from '@/seo/schema';

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={buildWebSiteSchema()} />
      <Navbar categories={categories} />
      <main>
        <Hero />
        <CategorySection categories={categories} />
        <ContentSections />
        <PillarArticlesSection />
        <ArticleCTA />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}

function PillarArticlesSection() {
  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
            Pillar Articles
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Basetao Spreadsheet Knowledge Base
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
            In-depth articles covering everything you need to know — from honest reviews and shipping strategies to warehouse processes and discount tips
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillarArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/${article.slug}`}
              className="group flex flex-col rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer hover:-translate-y-1"
            >
              <div className="aspect-[8/5] w-full overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - BasetaoSheet`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold">
                    {article.category}
                  </span>
                  <span className="text-[10px] text-foreground-400">{article.readTime}</span>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2 group-hover:text-primary-700 transition-colors leading-snug">
                  {article.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed flex-1">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-200/50">
                  <span className="text-[10px] text-foreground-400">{article.date}</span>
                  <span className="text-[10px] text-primary-600 font-medium flex items-center gap-1 ml-auto">
                    Read Article
                    <i className="ri-arrow-right-line text-[10px]"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
          Ready to Discover Better Basetao Resources?
        </h2>
        <p className="text-foreground-600 text-sm md:text-base mb-8 max-w-lg mx-auto">
          Access organized product data, detailed guides, and the latest shopping strategies — all in one place.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 rounded-xl text-base font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line mr-2"></i>
            basetao spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto px-8 py-3.5 rounded-xl text-base font-semibold bg-accent-500 text-background-50 hover:bg-accent-600 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line mr-2"></i>
            Buy It Now
          </a>
        </div>
      </div>
    </section>
  );
}